import React, { useEffect, useState } from "react";
import { Search, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { avatarSrc } from "@/lib/defaults";
import { VerifiedBadge } from "@/components/VerifiedBadge";

// Liste de membres gérée par le compte (lot 652 « Masquer ma story à », lot 655 « Amis proches ») :
// recherche (/search) → ajouter ; liste courante → retirer. `basePath` : GET (users), POST/DELETE {basePath}/{id}.
const Row = ({ u, dark, action, busy, onClick, testId, accent }) => (
  <div className={`flex items-center gap-2.5 rounded-xl px-2.5 py-2 ${dark ? "bg-white/5" : ""}`} style={dark ? undefined : { background: "var(--yalola-surface-alt)" }} data-testid={testId}>
    <Avatar className="h-8 w-8 shrink-0"><AvatarImage src={avatarSrc(u)} /><AvatarFallback className="text-xs">{u.name?.[0]}</AvatarFallback></Avatar>
    <span className="text-sm font-semibold truncate flex items-center gap-1 min-w-0 flex-1">{u.name}<VerifiedBadge user={u} size={12} /></span>
    <button type="button" onClick={onClick} disabled={busy} className={`shrink-0 inline-flex items-center gap-1 rounded-full px-2.5 h-7 text-xs font-semibold transition-colors disabled:opacity-50 ${dark ? "bg-white/15 text-white" : ""}`}
      style={dark ? undefined : { background: accent || "var(--yalola-primary)", color: "#fff" }} data-testid={`${testId}-action`}>
      {busy ? <Loader2 size={12} className="animate-spin" /> : action}
    </button>
  </div>
);

export const UserListPanel = ({ basePath, testPrefix, labels, tone = "light", accent, onCount }) => {
  const { t, user, refreshMe } = useApp();
  const dark = tone === "dark";
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [res, setRes] = useState([]);
  const [busy, setBusy] = useState(null);
  useEffect(() => { api.get(basePath).then(({ data }) => setItems(data.users || [])).catch(() => setItems([])); }, [basePath]);
  useEffect(() => { if (items) onCount?.(items.length); }, [items, onCount]);
  useEffect(() => {
    const s = q.trim();
    if (s.length < 2) { setRes([]); return undefined; }
    const id = setTimeout(async () => {
      try {
        const { data } = await api.get("/search", { params: { q: s } });
        const taken = new Set((items || []).map((u) => u.id));
        setRes((data.users || []).filter((u) => u.id !== user?.id && !taken.has(u.id) && !u.is_church).slice(0, 6));
      } catch { setRes([]); }
    }, 300);
    return () => clearTimeout(id);
  }, [q, items, user?.id]);
  const add = async (u) => {
    setBusy(u.id);
    try {
      await api.post(`${basePath}/${u.id}`);
      setItems((arr) => [...(arr || []), u]); setQ(""); setRes([]);
      toast.success(labels.added.replace("{name}", u.name || ""));
      refreshMe?.().catch(() => {});
    } catch (e) { toast.error(e?.friendlyMessage || e?.response?.data?.detail || t("common.error", "Erreur")); }
    finally { setBusy(null); }
  };
  const remove = async (u) => {
    setBusy(u.id);
    try {
      await api.delete(`${basePath}/${u.id}`);
      setItems((arr) => (arr || []).filter((x) => x.id !== u.id));
      toast.success(labels.removed.replace("{name}", u.name || ""));
      refreshMe?.().catch(() => {});
    } catch (e) { toast.error(e?.friendlyMessage || t("common.error", "Erreur")); }
    finally { setBusy(null); }
  };
  const mutedCls = dark ? "text-white/60" : "";
  const mutedStyle = dark ? undefined : { color: "var(--yalola-text-muted)" };
  return (
    <div data-testid={`${testPrefix}-panel`} data-count={(items || []).length}>
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none opacity-60" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("story.mention_search", "Rechercher un membre…")}
          className={`w-full rounded-full border pl-9 pr-3 h-10 text-sm outline-none ${dark ? "bg-white/10 border-white/15 text-white placeholder:text-white/50" : ""}`}
          style={dark ? undefined : { background: "var(--yalola-surface)", borderColor: "var(--yalola-border)", color: "var(--yalola-text)" }} data-testid={`${testPrefix}-search`} />
      </div>
      {q.trim().length > 0 && q.trim().length < 2 && <div className={`text-[11px] mt-1.5 ${mutedCls}`} style={mutedStyle}>{t("story.mention_search_hint", "Tape au moins 2 lettres")}</div>}
      {res.length > 0 && (
        <div className="mt-2 space-y-1" data-testid={`${testPrefix}-results`}>
          {res.map((u) => <Row key={u.id} u={u} dark={dark} accent={accent} busy={busy === u.id} onClick={() => add(u)} testId={`${testPrefix}-result-${u.id}`} action={labels.add} />)}
        </div>
      )}
      <div className={`text-[11px] font-semibold uppercase tracking-wider mt-4 mb-1.5 ${mutedCls}`} style={mutedStyle}>
        {labels.listTitle}{items?.length ? ` · ${t("story.hidden_from_count", "{n} personne(s)").replace("{n}", String(items.length))}` : ""}
      </div>
      {items === null ? <Loader2 size={16} className="animate-spin opacity-60" />
        : items.length === 0 ? <div className={`text-sm ${mutedCls}`} style={mutedStyle} data-testid={`${testPrefix}-empty`}>{labels.empty}</div>
        : <div className="space-y-1" data-testid={`${testPrefix}-list`}>{items.map((u) => <Row key={u.id} u={u} dark={dark} accent={accent} busy={busy === u.id} onClick={() => remove(u)} testId={`${testPrefix}-item-${u.id}`} action={labels.remove} />)}</div>}
    </div>
  );
};
